/**
 * Who is signed in — the session ACTOR — as main's identity helpers answer it.
 *
 * Main (#321) moved "who am I" from the Postgres `allData` blob to keyon's
 * MySQL self profile (GetSelfProfile), and every caller asks through
 * `sessionActorFrom`: privileges, messaging "From", order prescriber, note
 * writer, directives actor, chrome. On the port the signed-in person is the
 * access token's subject and `GET /me` describes them (`useSessionStore()`,
 * decision D4), so that is the only source here:
 *
 *  - the session store's profile, mapped to main's shape by the bridge's one
 *    mapper (api2/selfProfileShape.js `meToSelfProfile`), once `GET /me` has
 *    answered; before that, nobody;
 *  - the profile the caller passed only where there is no Pinia at all (unit
 *    specs that call these helpers bare). Wherever a Pinia is active the
 *    session decides alone: the sessionSelf store is filled from that same
 *    `/me` and from nothing else, so a profile it still holds when the session
 *    has none is a previous sign-in's, left behind when the token went away;
 *  - otherwise nobody: empty ids, `source: 'none'`.
 *
 * Never `allData`. It is the CLINICAL context: `allData.patient` is the open
 * chart, and with no chart open the port's blob carries no person there at
 * all. Main still fell back to it (manager while charting, else patient) when
 * its self profile had not loaded; on the port that fallback made a staff
 * member with no chart open into nobody (empty inbox, empty rosters) and a
 * clinician viewing a chart into that patient. A caller that needs the actor
 * before `/me` answers gets "nobody" and waits; it never gets a guess.
 */
import { getActivePinia } from 'pinia';
import { emptySelfProfile, normalizeSelfProfile } from '@/utils/selfProfileForm';
import { meToSelfProfile } from '@/api2/selfProfileShape';
import { useSessionStore } from '@/stores/session';

function str(v) {
  if (v === undefined || v === null) return '';
  return String(v).trim();
}

function hasSelf(profile) {
  if (!profile || typeof profile !== 'object') return false;
  return str(profile.unique_ID) !== '' && str(profile.accountnumber) !== '';
}

function displayNameFrom(first, last, fallback) {
  const name = `${str(first)} ${str(last)}`.trim();
  return name || str(fallback) || '';
}

/** The session store of the active Pinia, or null where there is no Pinia (bare unit specs). */
function sessionStore() {
  try {
    return getActivePinia() ? useSessionStore() : null;
  } catch (e) {
    return null;
  }
}

/**
 * The person at the keyboard behind an act-as token, in the self-profile
 * shape, or null when `/me` does not say who they are.
 *
 * Under RFC 8693 act-as (off by default; decision D4) `/me` describes the
 * EFFECTIVE user, the person acted as, and names the human in
 * `impersonating`. D4 says viewing someone never changes who is acting, so
 * the actor here is that human: their id and name, in the organization the
 * token is for (an act-as token never crosses one). Their phone and e-mail
 * are not on `/me`, so they are left empty rather than borrowed from the
 * person acted as. This is the same rule the bridge's getAllData follows for
 * `manager` (MSG-13).
 */
function actorBehind(me) {
  const who = me.impersonating || {};
  const name = str(who.actor_name).split(/\s+/).filter(Boolean);
  const actor = meToSelfProfile({
    id: who.actor_id,
    organization: me.organization,
    first_name: name[0] || '',
    last_name: name.slice(1).join(' '),
  });
  return hasSelf(actor) ? actor : null;
}

/**
 * The signed-in person in main's self-profile shape, or null when nobody is.
 *
 * Only `GET /me` names them. The store sets the id_token's claims together
 * with `me` (stores/session.js fetchSession), so there is no earlier moment
 * when the claims alone could; until `/me` answers the actor is nobody.
 */
function resolveSelf(sessionSelf) {
  const session = sessionStore();
  if (session) {
    if (!session.me) return null;
    if (session.me.impersonating) return actorBehind(session.me);
    const self = meToSelfProfile(session.me);
    return hasSelf(self) ? self : null;
  }
  return hasSelf(sessionSelf) ? normalizeSelfProfile(sessionSelf) : null;
}

/**
 * Stable actor contract for privileges, messaging, chrome, etc.
 *
 * `allData` is accepted so main's call sites keep their signature; it is not
 * read (see the file note).
 */
// eslint-disable-next-line no-unused-vars
export function sessionActorFrom(allData, sessionSelf) {
  const self = resolveSelf(sessionSelf);

  if (!self) {
    return {
      accountnumber: '',
      unique_ID: '',
      externId: '',
      externId2: '',
      firstName: '',
      lastName: '',
      email: '',
      phone1: '',
      username: '',
      displayName: '',
      source: 'none',
    };
  }

  return {
    accountnumber: self.accountnumber,
    unique_ID: self.unique_ID,
    externId: self.unique_ID,
    externId2: self.accountnumber,
    firstName: self.firstName,
    lastName: self.lastName,
    email: self.email,
    phone1: self.phone1,
    username: self.username,
    displayName: displayNameFrom(self.firstName, self.lastName, self.username),
    source: 'session',
  };
}

/**
 * Chrome / welcome display name: the signed-in person only, never `allData`
 * (which would show the chart patient, or a stale name). The session's own
 * `displayName` (preferred name, else full name) — the same text the account
 * menu shows — and blank until `/me` answers. The passed profile's name only
 * where there is no Pinia (see the file note).
 */
export function chromeDisplayNameFrom(sessionSelf) {
  const session = sessionStore();
  if (session) {
    if (!session.me) return '';
    // Under act-as, the human at the keyboard (see actorBehind).
    if (session.me.impersonating) return str(session.me.impersonating.actor_name);
    return session.displayName;
  }
  if (!hasSelf(sessionSelf)) return '';
  const self = normalizeSelfProfile(sessionSelf);
  return displayNameFrom(self.firstName, self.lastName, self.username);
}

/**
 * Shape used by Floating/Mobile message widgets: whose inbox, and the "From".
 * The signed-in person whether or not a chart is open (D4).
 */
export function messagingIdentityFrom(allData, sessionSelf) {
  const actor = sessionActorFrom(allData, sessionSelf);
  return {
    accountNumber: actor.accountnumber,
    subAccountNumber: actor.unique_ID,
    senderName: actor.displayName || 'Unknown User',
  };
}

/** Empty profile shaped for the store. */
export function emptySessionSelf() {
  return emptySelfProfile();
}
